import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { loadGoogleMapsApi, isGoogleMapsReady } from '@/lib/google-maps-loader';
import { buildHotelMapHoverHtml } from '@/lib/hotel-map-tooltip';
import { buildHotelMapMarkerIcon, buildHotelMapMarkerZIndex } from '@/lib/hotel-map-marker';
import { HotelWithSelection, Destination } from '@/types/hotel-search';
import { MapPin } from 'lucide-react';

/** Cores por município — não incluir voah-coral (#F4803B): reservada para pin "Escolhido". */
const MUNICIPALITY_COLORS = [
  '#007C82', // voah-teal
  '#059669', // emerald
  '#6366f1', // indigo
  '#a855f7', // purple
  '#ec4899', // pink
  '#0891b2', // cyan
  '#16a34a', // green-600
];

/** Laranja do design system (Voah coral) — pin de hotel escolhido. */
const CHOSEN_PIN_COLOR = '#F4803B';
/** Vermelho — pin de hotel favoritado. */
const FAVORITE_PIN_COLOR = '#dc2626';
/** Âmbar — legenda de recomendação editorial (forma estrela). */
const EDITORIAL_PICK_LEGEND_COLOR = '#f59e0b';

interface HotelMapProps {
  hotels: HotelWithSelection[];
  destinations: Destination[];
  activeMunicipality: string | null;
  /** Só muda quando entram/saem hotéis visíveis — dispara fitBounds. */
  boundsKey: string;
  highlightedHotelId: string | null;
  nightsByMunicipality?: Record<string, number>;
  onMarkerClick?: (hotelId: string) => void;
}

export function HotelMap({
  hotels,
  destinations,
  activeMunicipality,
  boundsKey,
  highlightedHotelId,
  nightsByMunicipality = {},
  onMarkerClick,
}: HotelMapProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<any>(null);
  const markersRef = useRef<Map<string, any>>(new Map());
  const infoWindowRef = useRef<any>(null);
  const hotelsRef = useRef(hotels);
  const visibleHotelsRef = useRef(hotels);
  const nightsRef = useRef(nightsByMunicipality);
  const onMarkerClickRef = useRef(onMarkerClick);
  const lastFitBoundsKeyRef = useRef<string | null>(null);
  const lastMunicipalityRef = useRef<string | null>(null);
  const [mapsLoaded, setMapsLoaded] = useState(false);

  hotelsRef.current = hotels;
  visibleHotelsRef.current = hotels;
  nightsRef.current = nightsByMunicipality;
  onMarkerClickRef.current = onMarkerClick;

  const municipalityColorMap = useMemo(
    () => destinations.reduce<Record<string, string>>((acc, dest, idx) => {
      acc[dest.municipality] = MUNICIPALITY_COLORS[idx % MUNICIPALITY_COLORS.length] ?? MUNICIPALITY_COLORS[0];
      return acc;
    }, {}),
    [destinations],
  );

  /** `hotels` já vem filtrado (município + Escolhidos/Favoritos) pelo pai. */
  const visibleHotels = hotels;

  useEffect(() => {
    let cancelled = false;

    if (isGoogleMapsReady(['maps'])) {
      setMapsLoaded(true);
      return;
    }

    loadGoogleMapsApi({ libraries: ['maps'] })
      .then(() => {
        if (!cancelled) setMapsLoaded(true);
      })
      .catch(err => console.error('Error loading Google Maps:', err));

    return () => { cancelled = true; };
  }, []);

  const initMap = useCallback(() => {
    if (!containerRef.current || !window.google?.maps) return;

    const map = new window.google.maps.Map(containerRef.current, {
      zoom: 6,
      center: { lat: 36.0, lng: 138.0 },
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false,
      zoomControlOptions: {
        position: window.google.maps.ControlPosition.RIGHT_CENTER,
      },
      styles: [
        { featureType: 'poi', elementType: 'labels', stylers: [{ visibility: 'off' }] },
        { featureType: 'transit', elementType: 'labels', stylers: [{ visibility: 'off' }] },
      ],
    });

    mapRef.current = map;
    infoWindowRef.current = new window.google.maps.InfoWindow({ disableAutoPan: true });
  }, []);

  const attachMarkerListeners = useCallback((marker: any, hotelId: string) => {
    if (!window.google?.maps) return;

    window.google.maps.event.clearListeners(marker, 'mouseover');
    window.google.maps.event.clearListeners(marker, 'mouseout');
    window.google.maps.event.clearListeners(marker, 'click');

    marker.addListener('mouseover', () => {
      const hotel = hotelsRef.current.find(h => h.hotel_id === hotelId);
      if (!hotel || !infoWindowRef.current || !mapRef.current) return;
      const nights = nightsRef.current[hotel.municipality];
      infoWindowRef.current.setContent(buildHotelMapHoverHtml(hotel, nights));
      infoWindowRef.current.open(mapRef.current, marker);
    });

    marker.addListener('mouseout', () => {
      infoWindowRef.current?.close();
    });

    marker.addListener('click', () => {
      infoWindowRef.current?.close();
      onMarkerClickRef.current?.(hotelId);
    });
  }, []);

  // Layout fixo/flex: o container pode ter 0×0 no primeiro paint — esperar dimensões reais.
  useEffect(() => {
    if (!mapsLoaded || mapRef.current || !containerRef.current) return;

    const el = containerRef.current;

    const tryInit = () => {
      if (el.offsetWidth > 0 && el.offsetHeight > 0 && !mapRef.current) {
        initMap();
        if (mapRef.current) {
          window.google?.maps?.event?.trigger(mapRef.current, 'resize');
        }
        return true;
      }
      return false;
    };

    if (tryInit()) return;

    const observer = new ResizeObserver(() => {
      if (tryInit()) observer.disconnect();
    });
    observer.observe(el);

    const fallback = window.setTimeout(() => {
      if (!mapRef.current && el.offsetWidth > 0) {
        initMap();
        window.google?.maps?.event?.trigger(mapRef.current, 'resize');
      }
    }, 500);

    return () => {
      observer.disconnect();
      window.clearTimeout(fallback);
    };
  }, [mapsLoaded, initMap]);

  const updateMarkers = useCallback(() => {
    if (!mapRef.current || !window.google?.maps) return;

    const currentIds = new Set(visibleHotels.map(h => h.hotel_id));

    // Remove stale markers
    markersRef.current.forEach((marker, id) => {
      if (!currentIds.has(id)) {
        marker.setMap(null);
        markersRef.current.delete(id);
      }
    });

    visibleHotels.forEach((hotel, idx) => {
      const position = { lat: hotel.coordinates.lat, lng: hotel.coordinates.lng };
      const municipalityColor = municipalityColorMap[hotel.municipality] ?? MUNICIPALITY_COLORS[0];
      const fillColor = hotel.selection.is_favorite
        ? FAVORITE_PIN_COLOR
        : hotel.selection.status === 'chosen'
          ? CHOSEN_PIN_COLOR
          : municipalityColor;
      const isHighlighted = hotel.hotel_id === highlightedHotelId;
      const label = {
        text: String(idx + 1),
        color: '#ffffff',
        fontWeight: 'bold' as const,
        fontSize: '11px',
      };

      let marker: any;

      if (markersRef.current.has(hotel.hotel_id)) {
        marker = markersRef.current.get(hotel.hotel_id)!;
        marker.setLabel(label);
        marker.setIcon(buildHotelMapMarkerIcon(window.google.maps, hotel, fillColor, isHighlighted));
        marker.setZIndex(buildHotelMapMarkerZIndex(hotel, idx, isHighlighted));
      } else {
        marker = new window.google.maps.Marker({
          position,
          map: mapRef.current,
          label,
          icon: buildHotelMapMarkerIcon(window.google.maps, hotel, fillColor, isHighlighted),
          zIndex: buildHotelMapMarkerZIndex(hotel, idx, isHighlighted),
        });

        markersRef.current.set(hotel.hotel_id, marker);
      }

      attachMarkerListeners(marker, hotel.hotel_id);
    });
  }, [visibleHotels, highlightedHotelId, municipalityColorMap, attachMarkerListeners]);

  const fitBoundsToVisibleHotels = useCallback(() => {
    const hotelsToFit = visibleHotelsRef.current;
    if (!mapRef.current || !window.google?.maps || hotelsToFit.length === 0) return;

    const bounds = new window.google.maps.LatLngBounds();
    hotelsToFit.forEach(hotel => {
      bounds.extend({ lat: hotel.coordinates.lat, lng: hotel.coordinates.lng });
    });

    if (hotelsToFit.length === 1) {
      mapRef.current.setCenter(bounds.getCenter());
      mapRef.current.setZoom(14);
    } else {
      mapRef.current.fitBounds(bounds, { top: 40, right: 40, bottom: 40, left: 40 });
    }
  }, []);

  useEffect(() => {
    if (!mapRef.current) return;

    const map = mapRef.current;
    const savedZoom = map.getZoom();
    const savedCenter = map.getCenter()?.toJSON();
    const boundsKeyAtUpdate = boundsKey;

    updateMarkers();

    // Escolher/favoritar/highlight só atualiza pins — preserva zoom e centro atuais.
    if (savedZoom != null && savedCenter && lastFitBoundsKeyRef.current === boundsKeyAtUpdate) {
      map.setCenter(savedCenter);
      map.setZoom(savedZoom);
    }
  }, [updateMarkers, boundsKey]);

  // Só reajusta zoom/centro quando entram/saem hotéis (filtro/município).
  useEffect(() => {
    if (!mapRef.current || !boundsKey || lastFitBoundsKeyRef.current === boundsKey) return;
    lastFitBoundsKeyRef.current = boundsKey;
    fitBoundsToVisibleHotels();
  }, [boundsKey, fitBoundsToVisibleHotels]);

  // Center on active municipality when filter changes (não repetir no mesmo município).
  useEffect(() => {
    if (!mapRef.current) return;
    if (!activeMunicipality) {
      lastMunicipalityRef.current = null;
      return;
    }
    if (lastMunicipalityRef.current === activeMunicipality) return;
    lastMunicipalityRef.current = activeMunicipality;

    const dest = destinations.find(d => d.municipality === activeMunicipality);
    if (dest) {
      mapRef.current.panTo({ lat: dest.coordinates.lat, lng: dest.coordinates.lng });
      mapRef.current.setZoom(13);
    }
  }, [activeMunicipality, destinations]);

  // Legend
  const activeDests = activeMunicipality
    ? destinations.filter(d => d.municipality === activeMunicipality)
    : destinations;

  return (
    <div className="relative w-full h-full min-h-[280px] rounded-xl overflow-hidden border border-gray-200 shadow-sm bg-gray-100">
      {!mapsLoaded && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-50 z-10">
          <div className="flex flex-col items-center gap-2 text-gray-400">
            <MapPin className="w-8 h-8 animate-pulse" />
            <span className="text-sm">Carregando mapa...</span>
          </div>
        </div>
      )}

      <div ref={containerRef} className="w-full h-full" />

      {/* Municipality legend */}
      {activeDests.length > 1 && (
        <div className="absolute bottom-3 left-3 bg-white/90 backdrop-blur-sm rounded-lg shadow-md p-2 flex flex-col gap-1.5">
          {activeDests.map(dest => (
            <div key={dest.municipality} className="flex items-center gap-2">
              <div
                className="w-3 h-3 rounded-full flex-shrink-0"
                style={{ backgroundColor: municipalityColorMap[dest.municipality] }}
              />
              <span className="text-xs font-medium text-gray-700">{dest.municipality}</span>
            </div>
          ))}
        </div>
      )}

      {/* Selection legend */}
      <div className="absolute bottom-3 right-3 bg-white/90 backdrop-blur-sm rounded-lg shadow-md p-2 flex flex-col gap-1.5">
        <div className="flex items-center gap-2">
          <svg viewBox="-2 -2 4 4" className="w-3 h-3 flex-shrink-0" aria-hidden>
            <path
              d="M 0,-1.55 L 0.47,-0.47 L 1.55,-0.47 L 0.62,0.22 L 0.98,1.55 L 0,0.78 L -0.98,1.55 L -0.62,0.22 L -1.55,-0.47 L -0.47,-0.47 Z"
              fill={EDITORIAL_PICK_LEGEND_COLOR}
              stroke="#ffffff"
              strokeWidth="0.2"
            />
          </svg>
          <span className="text-xs font-medium text-gray-700">Recomendação editorial</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: FAVORITE_PIN_COLOR }} />
          <span className="text-xs font-medium text-gray-700">Favorito</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: CHOSEN_PIN_COLOR }} />
          <span className="text-xs font-medium text-gray-700">Escolhido</span>
        </div>
      </div>

      {/* Hotels count badge */}
      <div className="absolute top-3 right-3 bg-white/90 backdrop-blur-sm rounded-lg shadow-md px-2.5 py-1.5">
        <span className="text-xs font-semibold text-gray-600">
          {visibleHotels.length} {visibleHotels.length === 1 ? 'hotel' : 'hotéis'}
        </span>
      </div>
    </div>
  );
}
